// Every in-app navigation runs the route guards, and a filter change is a navigation, so
// an uncached guard spends one of the 100 auth requests a minute that an office shares on
// one IP. Past that the server answers 429, which must not read as "signed out".

/** The check could not get an answer, so nobody can say whether this person is signed in. */
export class SessionUnknownError extends Error {
  constructor() {
    super("The session check failed");
    this.name = "SessionUnknownError";
  }
}

/**
 * Retry for the auth client. A 429 on the session check waits and asks again, for just over
 * the one-minute window of the limit, which is what frees a slot. Only that request: a sign-in
 * that was limited must say so at once.
 */
export const SESSION_CHECK_RETRY = {
  type: "exponential",
  attempts: 6,
  baseDelay: 1000,
  maxDelay: 30_000,
  shouldRetry: (response: Response | null) =>
    response?.status === 429 && new URL(response.url).pathname === "/api/auth/get-session",
} as const;

const FRESH_MS = 30_000;
// How long a user we last confirmed may stand in for a check that failed. Past this, a
// signed-out or removed user is not carried along by an outage or a rate limit.
const STALE_MS = 120_000;

interface SessionResult<S> {
  data: S | null;
  error?: unknown;
}

export function createSessionCache<S>(
  load: () => Promise<SessionResult<S>>,
  now: () => number = Date.now,
) {
  let last: { session: S; at: number } | null = null;

  return {
    /**
     * The session, or null only when the server really says there is none. A failed check with
     * no recent user to stand in throws SessionUnknownError: it is not a "signed out".
     */
    get: async (): Promise<S | null> => {
      if (last && now() - last.at < FRESH_MS) return last.session;
      const { data, error } = await load();
      if (data) {
        last = { session: data, at: now() };
        return data;
      }
      // A failed check is not an answer: keep who we knew, for a while. `/api/*` still enforces it.
      if (error && last && now() - last.at < STALE_MS) return last.session;
      last = null;
      if (error) throw new SessionUnknownError();
      return null;
    },
    clear: () => {
      last = null;
    },
  };
}
